/**
 * Quote management hooks
 *
 * These hooks wrap the auto-generated React Query hooks from cms-api
 * to provide UI-level concerns like toast notifications, router redirects,
 * and type transformations specific to our application.
 *
 * The underlying API calls use the generated types and functions from cms-api,
 * ensuring type safety and automatic updates when the API changes.
 */

import { ReactQuery } from 'cms-api'
import type { QuoteItemQuote } from 'cms-api/src/axios/cms'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { Toast } from 'components/Toast'

type Quote = QuoteItemQuote

/**
 * Hook for converting a cart to a quote
 * Wraps the generated React Query mutation with UI-level concerns like toasts and redirects
 */
export const useConvertCartToQuote = () => {
  const router = useRouter()

  const mutation = ReactQuery.usePostQuotesConvertFromCart({
    mutation: {
      onSuccess: (data) => {
        Toast.success('Quote created successfully!')

        // Redirect to quote page if we have a documentId
        if (data?.data?.documentId) {
          router.push(`/expert/quotes/${data.data.documentId}`)
        }
      },
      onError: (error: any) => {
        const errorMessage = error?.message || 'Failed to create quote'
        Toast.error(errorMessage)
      },
    },
  })

  const convertCartToQuote = (
    cartId: number,
    options?: { onSuccess?: (data: any) => void }
  ) =>
    mutation.mutate(
      { data: { cartId } },
      {
        onSuccess: (data) => {
          // Call custom onSuccess callback if provided
          if (options?.onSuccess && data?.data) {
            options.onSuccess(data.data)
          }
        },
      }
    )

  return {
    convertCartToQuote,
    loading: mutation.isLoading,
    error: mutation.error?.message || null,
  }
}

/**
 * Hook for sending a quote to a client
 * Wraps the generated React Query mutation with success/error toasts
 */
export const useSendQuote = () => {
  const mutation = ReactQuery.usePostQuotesIdSend({
    mutation: {
      onSuccess: () => {
        Toast.success('Quote sent successfully!')
      },
      onError: (error: any) => {
        const errorMessage = error?.message || 'Failed to send quote'
        Toast.error(errorMessage)
      },
    },
  })

  /** Resolves once the send has landed. Never rejects — `onError` reports it. */
  const sendQuote = async (quoteId: string): Promise<boolean> => {
    try {
      await mutation.mutateAsync({ id: quoteId })
      return true
    } catch {
      return false
    }
  }

  return {
    sendQuote,
    loading: mutation.isLoading,
    error: mutation.error?.message || null,
  }
}

/**
 * Hook for approving a quote
 * Wraps the generated React Query mutation with success/error toasts
 */
export const useApproveQuote = () => {
  const mutation = ReactQuery.usePostQuotesIdApprove({
    mutation: {
      onSuccess: () => {
        Toast.success('Quote approved successfully!')
      },
      onError: (error: any) => {
        const errorMessage = error?.message || 'Failed to approve quote'
        Toast.error(errorMessage)
      },
    },
  })

  const approveQuote = (quoteId: string) => mutation.mutate({ id: quoteId })

  return {
    approveQuote,
    loading: mutation.isLoading,
    error: mutation.error?.message || null,
  }
}

/**
 * Hook for cancelling a quote
 * Wraps the generated React Query mutation with success/error toasts
 */
export const useCancelQuote = () => {
  const mutation = ReactQuery.usePostQuotesIdCancel({
    mutation: {
      onSuccess: () => {
        Toast.success('Quote cancelled')
      },
      onError: (error: any) => {
        const errorMessage = error?.message || 'Failed to cancel quote'
        Toast.error(errorMessage)
      },
    },
  })

  const cancelQuote = (quoteId: string) => mutation.mutate({ id: quoteId })

  return {
    cancelQuote,
    loading: mutation.isLoading,
    error: mutation.error?.message || null,
  }
}

/**
 * Hook for fetching a quote by ID
 * Wraps the generated React Query hook with:
 * - Type transformation to match our custom Quote interface
 * - Sensible defaults for missing data
 */
export const useQuote = (quoteId: string | null) => {
  // Use ReactQuery's useGetQuotesId hook
  // Strapi has wrong schema, documentId is string not number https://github.com/strapi/strapi/issues/22599
  const { data, isLoading, error, refetch } = ReactQuery.useGetQuotesId(
    quoteId as any,
    {
      query: {
        enabled: !!quoteId,
        staleTime: 5 * 60 * 1000, // 5 minutes
      },
    }
  )

  // Use the generated Quote type directly from the API response
  const quote: Quote | null = data?.data || null

  return {
    quote,
    loading: isLoading,
    error: error?.message || null,
    refetch,
  }
}

/**
 * Hook to fetch all clients for the current expert
 */
export const useExpertClients = () => {
  const { data, isLoading, error } = ReactQuery.useGetQuotesClients({
    query: {
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  })

  return {
    clients: data?.data || [],
    loading: isLoading,
    error,
  }
}

/**
 * Hook to assign a client to a quote
 */
export const useAssignClientToQuote = () => {
  const mutation = ReactQuery.usePostQuotesIdAssignClient()

  const assignClient = (
    quoteId: string,
    data: {
      clientId?: number
      title: string
      shippingCost?: {
        base: number
        vat: number
        vatRate: number
        total: number
        currencyIsoCode: string
      }
      selectedShippingPerItem?: {
        [productVariantId: number]: {
          type: string
          name: string
          price: {
            base: number
            vat: number
            vatRate: number
            total: number
            currencyIsoCode: string
          }
        }
      }
      createClient?: {
        firstName: string
        lastName: string
        email: string
        phone?: string
        businessName?: string
        addressLine1: string
        addressLine2?: string
        city: string
        countryIsoCode: string
        countryState?: string
        zipCode: string
      }
    }
  ) => mutation.mutateAsync({ id: quoteId, data })

  return {
    assignClient,
    isAssigning: mutation.isLoading || false,
    error: mutation.error,
  }
}

/**
 * Hook to update a quote
 * Wraps the generated React Query mutation with success/error toasts
 */
/**
 * A correction to the client's own record, made from their quote. The client's
 * name lives on the client, not on the quote, so a typo made while assigning
 * the quote can only be fixed here.
 */
export interface QuoteClientDetails {
  firstName?: string
  lastName?: string
  phone?: string
  /** The client's contact address, not their login identity. */
  contactEmail?: string
}

export const useUpdateQuote = () => {
  const mutation = ReactQuery.usePutQuotesId({
    mutation: {
      onError: (error: any) => {
        const errorMessage = error?.message || 'Failed to update quote'
        Toast.error(errorMessage)
      },
    },
  })

  /**
   * Resolves once the write has actually landed, so a caller that closes a
   * panel or refetches afterwards does not race the request. `mutate` returned
   * immediately, which let the quote page reload while the PUT was still in
   * flight and show the pre-edit values back.
   *
   * Never rejects: `onError` above already tells the user. Most callers are
   * fire-and-forget toggles, and a rejection they do not catch would surface as
   * an unhandled rejection rather than anything useful.
   */
  const updateQuote = async (
    quoteId: string,
    data: {
      hideTradePricing?: boolean
      isTaxExemptionApplied?: boolean
      title?: string
      notes?: string
      validUntil?: string
      client?: QuoteClientDetails
    }
  ): Promise<boolean> => {
    try {
      await mutation.mutateAsync({
        id: quoteId as any, // Strapi uses string documentId but generated types expect number
        data: { data } as any, // Partial update, generated types require all fields
      })
      return true
    } catch {
      return false
    }
  }

  return {
    updateQuote,
    loading: mutation.isLoading,
    error: mutation.error?.message || null,
  }
}

/**
 * Hook for deleting (soft delete) a quote
 * Wraps the generated React Query mutation with success/error toasts
 */
export const useDeleteQuote = () => {
  const mutation = ReactQuery.useDeleteQuotesId({
    mutation: {
      onSuccess: () => {
        Toast.success('Quote deleted successfully')
      },
      onError: (error: any) => {
        const errorMessage = error?.message || 'Failed to delete quote'
        Toast.error(errorMessage)
      },
    },
  })

  const deleteQuote = (quoteId: string, options?: { onSuccess?: () => void }) =>
    mutation.mutate(
      { id: quoteId as any },
      {
        onSuccess: () => {
          options?.onSuccess?.()
        },
      }
    )

  return {
    deleteQuote,
    loading: mutation.isLoading,
    error: mutation.error?.message || null,
  }
}

/**
 * Response types for convert-to-cart operations
 */
interface ConvertToCartResponse {
  success?: boolean
  cartId?: number
  requiresChoice?: boolean
  activeCartId?: number
  activeCartItemCount?: number
}

/**
 * Hook for converting a quote back to a cart
 * Handles the flow where an expert may need to choose between merging or replacing their existing cart
 */
export const useConvertQuoteToCart = () => {
  // Check conversion mutation - determines if direct conversion or choice needed
  const checkMutation = ReactQuery.usePostQuotesIdConvertToCart({
    mutation: {
      onError: (error: any) => {
        const errorMessage = error?.message || 'Failed to convert quote to cart'
        Toast.error(errorMessage)
      },
    },
  })

  // Merge mutation - copies quote items into existing active cart
  const mergeMutation = ReactQuery.usePostQuotesIdConvertToCartMerge({
    mutation: {
      onError: (error: any) => {
        const errorMessage = error?.message || 'Failed to merge items'
        Toast.error(errorMessage)
      },
    },
  })

  // Replace mutation - abandons active cart and activates quote's cart
  const replaceMutation = ReactQuery.usePostQuotesIdConvertToCartReplace({
    mutation: {
      onError: (error: any) => {
        const errorMessage = error?.message || 'Failed to replace cart'
        Toast.error(errorMessage)
      },
    },
  })

  /**
   * Check if the quote can be converted directly or if user needs to make a choice
   */
  const checkConversion = async (
    quoteId: string
  ): Promise<ConvertToCartResponse> => {
    const result = await checkMutation.mutateAsync({ id: quoteId })
    return result?.data || {}
  }

  /**
   * Merge quote items into the expert's existing active cart
   */
  const mergeToCart = async (
    quoteId: string
  ): Promise<ConvertToCartResponse> => {
    const result = await mergeMutation.mutateAsync({ id: quoteId })
    return result?.data || {}
  }

  /**
   * Replace the expert's active cart with the quote's cart
   */
  const replaceCart = async (
    quoteId: string
  ): Promise<ConvertToCartResponse> => {
    const result = await replaceMutation.mutateAsync({ id: quoteId })
    return result?.data || {}
  }

  return {
    checkConversion,
    mergeToCart,
    replaceCart,
    loading:
      checkMutation.isLoading ||
      mergeMutation.isLoading ||
      replaceMutation.isLoading,
    error:
      checkMutation.error?.message ||
      mergeMutation.error?.message ||
      replaceMutation.error?.message ||
      null,
  }
}

/**
 * Hook for applying a promo code to a quote
 */
export const useApplyQuotePromoCode = () => {
  const mutation = ReactQuery.usePostQuotesIdApplyPromoCode({
    mutation: {
      onSuccess: () => {
        Toast.success('Promo code applied!')
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.message ||
          'Invalid promo code'
        Toast.error(errorMessage)
      },
    },
  })

  const applyPromoCode = (quoteId: string, code: string) =>
    mutation.mutateAsync({ id: quoteId, data: { code } } as any)

  return {
    applyPromoCode,
    loading: mutation.isLoading,
    error: mutation.error,
  }
}

/**
 * Hook for removing a promo code from a quote
 */
export const useRemoveQuotePromoCode = () => {
  const mutation = ReactQuery.usePostQuotesIdRemovePromoCode({
    mutation: {
      onSuccess: () => {
        Toast.success('Promo code removed')
      },
      onError: (error: any) => {
        const errorMessage = error?.message || 'Failed to remove promo code'
        Toast.error(errorMessage)
      },
    },
  })

  const removePromoCode = (quoteId: string) =>
    mutation.mutateAsync({ id: quoteId } as any)

  return {
    removePromoCode,
    loading: mutation.isLoading,
    error: mutation.error,
  }
}

/**
 * Hooks for the quote edit session.
 *
 * These two endpoints are not in the generated client yet: orval reads the
 * committed Strapi OpenAPI document, which only picks up new custom routes the
 * next time Strapi boots. Regenerating also means rebuilding the cms-api dist,
 * which needs Node 18. So they call Strapi directly, with the same Firebase
 * bearer token the generated client uses — move them onto `ReactQuery` once
 * codegen has run.
 */
const callQuoteEndpoint = async <T>(
  path: string,
  body: unknown
): Promise<T> => {
  const { getApp } = await import('firebase/app')
  const { getAuth } = await import('firebase/auth')
  const token = await getAuth(getApp()).currentUser?.getIdToken()

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_STRAPI_URL}/api${path}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    }
  )

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    // Strapi puts the reason in error.message; these endpoints reject with a
    // specific one (wrong status, an item missing from the CMS) worth showing
    throw new Error(
      payload?.error?.message || payload?.message || 'Request failed'
    )
  }

  return payload?.data as T
}

export interface QuoteEditItem {
  productVariantId: number
  quantity: number
}

/**
 * A line the expert wrote by hand rather than picked from the catalogue.
 *
 * It carries one price, which the backend writes to both trade price and MSRP,
 * and it never reaches the cart — so it shows on the quote and its PDF but is
 * absent from shipping, tax and anything the client checks out.
 */
export interface QuoteCustomItem {
  title: string
  price: number
  quantity: number
  description?: string | null
  /** Numeric upload file id — a Strapi media relation connects by id, not documentId. */
  imageId?: number | null
  imageUrl?: string | null
  leadTimeInWeeks?: number | null
}

/**
 * Upload one image for a custom item and return the id the edit payload carries.
 *
 * Unused while custom items are switched off — the Strapi route it posts to is
 * removed too, so restoring the feature means putting both back alongside
 * QuoteCustomItems.
 */
const uploadCustomItemImage = async (
  quoteId: string,
  file: File
): Promise<{ id: number; url: string }> => {
  const { getApp } = await import('firebase/app')
  const { getAuth } = await import('firebase/auth')
  const token = await getAuth(getApp()).currentUser?.getIdToken()

  const form = new FormData()
  form.append('files', file)

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_STRAPI_URL}/api/quotes/${quoteId}/custom-item-image`,
    {
      method: 'POST',
      // No Content-Type — the browser has to set the multipart boundary
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    }
  )

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    throw new Error(
      payload?.error?.message || payload?.message || 'Upload failed'
    )
  }

  return payload?.data
}

/**
 * Opens an edit session for a quote's items.
 *
 * Returns the id of a scratch cart holding the desired items. Shipping is
 * priced from what is in a cart, and pricing the quote's own cart would leave
 * the quote inconsistent if the edit were abandoned.
 */
export const useQuoteEditSession = () => {
  const [isStarting, setIsStarting] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const startEditSession = async (
    quoteId: string,
    items: QuoteEditItem[]
  ): Promise<number | null> => {
    setIsStarting(true)
    try {
      const data = await callQuoteEndpoint<{ cartId: number }>(
        `/quotes/${quoteId}/edit-session`,
        { items }
      )
      return data?.cartId ?? null
    } catch (error) {
      Toast.error((error as Error).message)
      return null
    } finally {
      setIsStarting(false)
    }
  }

  const applyEdit = async (
    quoteId: string,
    payload: {
      cartId: number
      customItems?: QuoteCustomItem[]
      shippingMode?: 'CALCULATED' | 'NONE' | 'TBD'
      shippingAddress?: unknown
      billingAddress?: unknown
      shippingCost?: unknown
      taxAmount?: unknown
      isTaxExemptionApplied?: boolean
      title?: string
      notes?: string
      validUntil?: string
      client?: QuoteClientDetails
      selectedShippingPerItem?: Record<number, unknown>
    }
  ): Promise<boolean> => {
    setIsSaving(true)
    try {
      await callQuoteEndpoint(`/quotes/${quoteId}/apply-edit`, payload)
      Toast.success('Quote updated')
      return true
    } catch (error) {
      Toast.error((error as Error).message)
      return false
    } finally {
      setIsSaving(false)
    }
  }

  return {
    startEditSession,
    applyEdit,
    uploadCustomItemImage,
    isStarting,
    isSaving,
  }
}
